import { Component, DestroyRef, inject, OnInit } from "@angular/core";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { UserLoginService } from "src/app/login/userlogin.service";

import { HospitalUserAffiliationService } from "src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service";

import { HospitalUserAffiliationReadByUserIdRequestDTO } from "src/app/hospital/HospitalUserMapping/user-affiliation-mapping.interface";

import { ToastrService } from "ngx-toastr";

import { HospitalMasterService } from "src/app/hospital/BackOffice/hospital-master/hospital-master.service";

import { RevenueReportServiceService } from "../revenue-report.service.service";

import {
  DoctorRevenueSummary,
  RevenueReportCategory,
  RevenueReportRow,
  RevenueReportSummary,
} from "../revenue-report-interface";

/*
 * NEW:
 * Existing hospital module already uses this exact service
 * for doctor listing.
 */
import { UsersService } from "src/app/master/users/users.service";

import { UserMasterReadByHospitalId } from "src/app/master/users/users.interface";

@Component({
  selector: "app-revenue-report-for-owner",

  templateUrl: "./revenue-report-for-owner.component.html",

  styleUrls: ["./revenue-report-for-owner.component.css"],
})
export class RevenueReportForOwnerComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  private User = inject(UserLoginService).User();

  private hospitalUserAffiliationService = inject(
    HospitalUserAffiliationService,
  );

  private hospitalMasterService = inject(HospitalMasterService);

  private revenueReportService = inject(RevenueReportServiceService);

  private toastr = inject(ToastrService);

  /*
   * NEW:
   * Existing UserMaster service.
   * Used only for resolving doctor names for this hospital.
   */
  private userMasterService = inject(UsersService);

  hospitalId = 0;

  hospitalName = "";
  hospitalAddress = "";

  loading = false;

  exportingExcel = false;
  exportingPdf = false;

  /*
   * NEW:
   * DoctorId -> Doctor Name
   *
   * Only doctors belonging to the owner's hospital
   * will be loaded here.
   */
  private hospitalDoctorMap = new Map<number, string>();

  private doctorsLoaded = false;

  reportTabs: {
    label: string;
    value: RevenueReportCategory;
  }[] = [
    {
      label: "All Revenue",

      value: "ALL",
    },
    {
      label: "OPD",

      value: "OPD",
    },
    {
      label: "IPD",

      value: "IPD",
    },
    {
      label: "Doctor",

      value: "DOCTOR",
    },
  ];

  activeCategory: RevenueReportCategory = "ALL";

  fromDate = "";
  toDate = "";

  searchText = "";

  selectedDoctor = "ALL";

  allRows: RevenueReportRow[] = [];

  filteredRows: RevenueReportRow[] = [];

  doctorOptions: string[] = [];

  doctorSummary: DoctorRevenueSummary[] = [];

  summary: RevenueReportSummary = {
    totalRevenue: 0,

    opdRevenue: 0,

    ipdRevenue: 0,

    totalPatients: 0,

    opdPatients: 0,

    ipdPatients: 0,

    doctorCount: 0,
  };

  pageNo = 1;

  pageSize = 20;

  pageSizeOptions = [20, 50, 100, 200];

  ngOnInit(): void {
    this.setDefaultDates();

    this.loadOwnerHospital();
  }

  private setDefaultDates(): void {
    const today = new Date();

    this.fromDate = this.formatDateForInput(today);

    this.toDate = this.formatDateForInput(today);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();

    const month = String(date.getMonth() + 1).padStart(2, "0");

    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // ============================================================
  // OWNER / HOSPITAL
  // ============================================================

  private loadOwnerHospital(): void {
    /*
     * SINGLE API FIX:
     *
     * RevenueReport/Create already receives logged-in UserId and the
     * hm.RevenueReportForOwner_Read stored procedure resolves/validates
     * the owner's hospital on the server.
     *
     * Therefore this page does NOT need to call:
     * - UserAffiliationMapping_ReadByUserId
     * - HospitalMaster_ReadById
     * - UserMaster_ReadByHospitalId
     * before loading the report.
     *
     * Keep the existing code below as a legacy fallback, but return
     * here so the current Revenue Report screen makes one report API call.
     */
    const useSingleRevenueReportApi = true;

    if (useSingleRevenueReportApi) {
      this.hospitalId = 0;

      this.loadReport();

      return;
    }

    const payload: HospitalUserAffiliationReadByUserIdRequestDTO = {
      userId: this.User.userId,
    };

    this.hospitalUserAffiliationService
      .HospitalUserAffiliationReadByUserId(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          /*
           * Owner hospital is taken ONLY from logged-in UserId.
           *
           * No hospitalId comes from URL,
           * query string or user-selected value.
           */
          const ownerHospital = (res?.items || []).find(
            (item: any) =>
              Number(item?.hospitalId ?? item?.HospitalId ?? 0) > 0,
          );

          this.hospitalId = Number(
            ownerHospital?.hospitalId ?? ownerHospital?.HospitalId ?? 0,
          );

          if (!this.hospitalId) {
            this.allRows = [];

            this.filteredRows = [];

            this.doctorOptions = [];

            this.doctorSummary = [];

            this.calculateSummary();

            this.toastr.warning("Hospital not found for this owner.");

            return;
          }

          /*
           * Load ONLY owner's hospital information.
           */
          this.loadHospitalInfo();

          /*
           * NEW:
           * Load doctors belonging only to this hospital.
           */
          this.loadHospitalDoctors();

          /*
           * Existing report flow.
           */
          this.loadReport();
        },

        error: (err) => {
          console.error("Owner affiliation error:", err);

          this.allRows = [];

          this.filteredRows = [];

          this.toastr.error("Unable to load hospital information.");
        },
      });
  }

  private loadHospitalInfo(): void {
    this.hospitalMasterService
      .HospitalMaster_ReadById({
        hospitalId: this.hospitalId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.hospitalName = res?.hospitalName || "";

          this.hospitalAddress = res?.address || "";
        },

        error: (err) => {
          console.error("Hospital info error:", err);
        },
      });
  }

  // ============================================================
  // NEW - LOAD HOSPITAL DOCTORS
  // ============================================================

  private loadHospitalDoctors(): void {
    if (!this.hospitalId) {
      return;
    }

    const data: UserMasterReadByHospitalId = {
      hospitalId: this.hospitalId,

      affiliationType: "Doctor",

      pageSize: 10000,

      pageNo: 1,

      orderByClause: "",

      whereClause: "",
    };

    this.userMasterService
      .UserMaster_ReadByHospitalId(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.hospitalDoctorMap.clear();

          const doctors = Array.isArray(res?.items) ? res.items : [];

          doctors.forEach((doctor: any) => {
            const doctorId = Number(doctor?.userId ?? doctor?.UserId ?? 0);

            if (!doctorId) {
              return;
            }

            const firstName = String(
              doctor?.firstName ?? doctor?.FirstName ?? "",
            ).trim();

            const middleName = String(
              doctor?.middleName ?? doctor?.MiddleName ?? "",
            ).trim();

            const lastName = String(
              doctor?.lastName ?? doctor?.LastName ?? "",
            ).trim();

            const completeName = [firstName, middleName, lastName]
              .filter(Boolean)
              .join(" ")
              .trim();

            if (completeName) {
              this.hospitalDoctorMap.set(doctorId, completeName);
            }
          });

          this.doctorsLoaded = true;

          /*
           * Report may already be loaded before doctors API completes.
           * Update existing rows without reloading invoice APIs.
           */
          this.resolveDoctorNames();

          this.buildDoctorOptions();

          this.applyFilters();
        },

        error: (err) => {
          console.error("Hospital doctor load error:", err);

          this.doctorsLoaded = true;
        },
      });
  }

  // ============================================================
  // NEW - RESOLVE DOCTOR NAMES
  // ============================================================

  private resolveDoctorNames(): void {
    if (!this.allRows.length) {
      return;
    }

    this.allRows = this.allRows.map((row) => {
      /*
       * Another final HospitalId safeguard.
       */
      if (Number(row.hospitalId || 0) !== Number(this.hospitalId)) {
        return row;
      }

      const doctorId = Number(row.doctorId || 0);

      /*
       * If InvoiceDetail already returned staffName,
       * don't overwrite it.
       */
      const existingName = String(row.doctorName || "").trim();

      if (existingName && existingName !== "-") {
        return row;
      }

      /*
       * Resolve StaffId against doctors belonging
       * to ONLY this hospital.
       */
      const mappedName = this.hospitalDoctorMap.get(doctorId);

      if (mappedName) {
        return {
          ...row,

          doctorName: mappedName,
        };
      }

      return row;
    });
  }

  // ============================================================
  // LOAD REPORT
  // ============================================================

  loadReport(): void {
    const loggedInUserId = Number(this.User?.userId || 0);

    if (!loggedInUserId) {
      this.toastr.warning("Logged-in user not found.");

      return;
    }

    if (!this.fromDate || !this.toDate) {
      this.toastr.warning("Please select From Date and To Date.");

      return;
    }

    const from = this.parseInputDate(this.fromDate);

    const to = this.parseInputDate(this.toDate);

    if (!from || !to) {
      this.toastr.warning("Please select valid dates.");

      return;
    }

    if (from.getTime() > to.getTime()) {
      this.toastr.warning("From Date cannot be greater than To Date.");

      return;
    }

    this.loading = true;

    this.revenueReportService
      .getRevenueReport({
        /*
         * SINGLE API FIX:
         * UserId is sent to RevenueReport/Create.
         * When hospitalId is 0 on first load, backend resolves the owner's
         * hospital from hm.UserAffiliationMapping and validates access.
         * On subsequent loads, resolved HospitalId is sent back as before.
         */
        userId: loggedInUserId,

        hospitalId: this.hospitalId,

        fromDate: this.fromDate,

        toDate: this.toDate,

        category: "ALL",
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows: RevenueReportRow[]) => {
          const reportRows = Array.isArray(rows) ? rows : [];

          /*
           * SINGLE API FIX:
           * Hospital information is already returned by
           * RevenueReport/Create together with each revenue row.
           * Resolve it before applying the existing hospital safeguard.
           */
          const firstRow: any = reportRows[0] || null;

          if (firstRow) {
            const returnedHospitalId = Number(
              firstRow?.hospitalId ?? firstRow?.HospitalId ?? 0,
            );

            if (returnedHospitalId > 0) {
              this.hospitalId = returnedHospitalId;
            }

            this.hospitalName = String(
              firstRow?.hospitalName ??
                firstRow?.HospitalName ??
                this.hospitalName ??
                "",
            ).trim();

            this.hospitalAddress = String(
              firstRow?.hospitalAddress ??
                firstRow?.HospitalAddress ??
                this.hospitalAddress ??
                "",
            ).trim();
          }

          /*
           * Existing client-side hospital safeguard remains unchanged in
           * intent. If the first response is empty there is simply no row
           * to display.
           */
          this.allRows = reportRows.filter(
            (row) =>
              !this.hospitalId ||
              Number(row.hospitalId || 0) === Number(this.hospitalId),
          );

          /*
           * Existing doctor-name resolver remains in the flow.
           * The new RevenueReport API already supplies doctorName, so this
           * method normally leaves the returned name unchanged.
           */
          this.resolveDoctorNames();

          this.buildDoctorOptions();

          this.applyFilters();

          this.loading = false;
        },

        error: (err) => {
          console.error("Revenue Report Error:", err);

          this.allRows = [];

          this.filteredRows = [];

          this.doctorOptions = [];

          this.doctorSummary = [];

          this.calculateSummary();

          this.loading = false;

          this.toastr.error("Failed to load revenue report.");
        },
      });
  }

  // ============================================================
  // TABS
  // ============================================================

  setCategory(category: RevenueReportCategory): void {
    this.activeCategory = category;

    this.selectedDoctor = "ALL";

    this.searchText = "";

    this.pageNo = 1;

    this.applyFilters();
  }

  // ============================================================
  // FILTER REPORT
  // ============================================================

  applyFilters(): void {
    /*
     * First: Owner's hospital only.
     */
    let rows = this.allRows.filter(
      (row) => Number(row.hospitalId || 0) === Number(this.hospitalId),
    );

    /*
     * Revenue means actual paid invoices only.
     */
    rows = rows.filter(
      (row: RevenueReportRow) =>
        String(row.status || "")
          .trim()
          .toLowerCase() === "paid",
    );

    if (this.activeCategory === "OPD") {
      rows = rows.filter(
        (row) =>
          String(row.patientType || "")
            .trim()
            .toUpperCase() === "OPD",
      );
    }

    if (this.activeCategory === "IPD") {
      rows = rows.filter(
        (row) =>
          String(row.patientType || "")
            .trim()
            .toUpperCase() === "IPD",
      );
    }

    if (this.activeCategory === "DOCTOR") {
      rows = rows.filter(
        (row) =>
          Number(row.doctorId || 0) > 0 ||
          (!!String(row.doctorName || "").trim() &&
            String(row.doctorName).trim() !== "-"),
      );
    }

    if (this.selectedDoctor !== "ALL") {
      rows = rows.filter(
        (row) =>
          String(row.doctorName || "")
            .trim()
            .toLowerCase() === String(this.selectedDoctor).trim().toLowerCase(),
      );
    }

    const search = String(this.searchText || "")
      .trim()
      .toLowerCase();

    if (search) {
      rows = rows.filter(
        (row) =>
          String(row.patientName || "")
            .toLowerCase()
            .includes(search) ||
          String(row.invoiceNumber || "")
            .toLowerCase()
            .includes(search) ||
          String(row.doctorName || "")
            .toLowerCase()
            .includes(search) ||
          String(row.paymentMode || "")
            .toLowerCase()
            .includes(search),
      );
    }

    rows.sort(
      (a, b) =>
        this.getDateTime(b.receivedDate) - this.getDateTime(a.receivedDate),
    );

    this.filteredRows = rows;

    this.pageNo = 1;

    this.calculateSummary();

    this.buildDoctorSummary();
  }

  onDoctorFilterChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.searchText = "";

    this.selectedDoctor = "ALL";

    this.applyFilters();
  }

  onDateChange(): void {
    if (this.fromDate && this.toDate) {
      this.loadReport();
    }
  }

  private buildDoctorOptions(): void {
    const paidRows = this.allRows.filter(
      (row) =>
        Number(row.hospitalId || 0) === Number(this.hospitalId) &&
        String(row.status || "")
          .trim()
          .toLowerCase() === "paid",
    );

    this.doctorOptions = Array.from(
      new Set(
        paidRows
          .map((row) => String(row.doctorName || "").trim())
          .filter((name) => !!name && name !== "-"),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }

  // ============================================================
  // SUMMARY
  // ============================================================

  private calculateSummary(): void {
    const rows = this.filteredRows;

    const opdRows = rows.filter(
      (row) =>
        String(row.patientType || "")
          .trim()
          .toUpperCase() === "OPD",
    );

    const ipdRows = rows.filter(
      (row) =>
        String(row.patientType || "")
          .trim()
          .toUpperCase() === "IPD",
    );

    const uniqueDoctors = new Set(
      rows
        .map((row) => String(row.doctorName || "").trim())
        .filter((doctor) => !!doctor && doctor !== "-"),
    );

    this.summary = {
      totalRevenue: rows.reduce(
        (sum, row) => sum + Number(row.totalReceived || 0),
        0,
      ),

      opdRevenue: opdRows.reduce(
        (sum, row) => sum + Number(row.totalReceived || 0),
        0,
      ),

      ipdRevenue: ipdRows.reduce(
        (sum, row) => sum + Number(row.totalReceived || 0),
        0,
      ),

      totalPatients: rows.length,

      opdPatients: opdRows.length,

      ipdPatients: ipdRows.length,

      doctorCount: uniqueDoctors.size,
    };
  }

  // ============================================================
  // DOCTOR REPORT
  // ============================================================

  private buildDoctorSummary(): void {
    const map = new Map<string, DoctorRevenueSummary>();

    this.filteredRows.forEach((row) => {
      const doctorName = String(row.doctorName || "Not Assigned").trim();

      if (!map.has(doctorName)) {
        map.set(doctorName, {
          doctorId: Number(row.doctorId || 0),

          doctorName,

          patientCount: 0,

          opdCount: 0,

          ipdCount: 0,

          totalReceived: 0,
        });
      }

      const item = map.get(doctorName)!;

      item.patientCount++;

      const type = String(row.patientType || "")
        .trim()
        .toUpperCase();

      if (type === "OPD") {
        item.opdCount++;
      }

      if (type === "IPD") {
        item.ipdCount++;
      }

      item.totalReceived += Number(row.totalReceived || 0);
    });

    this.doctorSummary = Array.from(map.values()).sort(
      (a, b) => b.totalReceived - a.totalReceived,
    );
  }

  // ============================================================
  // PAGINATION
  // ============================================================

  get paginatedRows(): RevenueReportRow[] {
    const start = (this.pageNo - 1) * this.pageSize;

    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    if (!this.filteredRows.length) {
      return 0;
    }

    return Math.ceil(this.filteredRows.length / this.pageSize);
  }

  get startRecord(): number {
    if (!this.filteredRows.length) {
      return 0;
    }

    return (this.pageNo - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    return Math.min(
      this.pageNo * this.pageSize,

      this.filteredRows.length,
    );
  }

  get visiblePageNumbers(): number[] {
    const pages: number[] = [];

    if (!this.totalPages) {
      return pages;
    }

    const maxVisible = 5;

    let start = Math.max(1, this.pageNo - 2);

    let end = Math.min(
      this.totalPages,

      start + maxVisible - 1,
    );

    if (end - start + 1 < maxVisible) {
      start = Math.max(
        1,

        end - maxVisible + 1,
      );
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.pageNo = page;
  }

  onPageSizeChange(event: Event): void {
    const size = Number((event.target as HTMLSelectElement).value);

    if (!size) {
      return;
    }

    this.pageSize = size;

    this.pageNo = 1;
  }

  // ============================================================
  // FORMAT
  // ============================================================

  formatMoney(amount: number): string {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",

      currency: "INR",

      minimumFractionDigits: 2,
    }).format(Number(amount || 0));
  }

  getPatientTypeLabel(type: string): string {
    return String(type || "")
      .trim()
      .toUpperCase() === "OPD"
      ? "Out-Patient (OPD)"
      : "In-Patient (IPD)";
  }

  // ============================================================
  // EXCEL
  // ============================================================

  exportExcel(): void {
    if (!this.filteredRows.length) {
      this.toastr.warning("No revenue data available to export.");

      return;
    }

    this.exportingExcel = true;

    try {
      const rows = this.filteredRows
        .map(
          (row, index) => `
          <tr>
            <td>${index + 1}</td>

            <td>
              ${this.escapeHtml(this.formatDisplayDate(row.receivedDate))}
            </td>

            <td>
              ${this.escapeHtml(row.invoiceNumber)}
            </td>

            <td>
              ${this.escapeHtml(row.patientName)}
            </td>

            <td>
              ${this.escapeHtml(row.patientType)}
            </td>

            <td>
              ${this.escapeHtml(row.doctorName)}
            </td>

            <td>
              ${this.escapeHtml(row.paymentMode)}
            </td>

            <td>
              ${Number(row.totalReceived || 0).toFixed(2)}
            </td>
          </tr>
        `,
        )
        .join("");

      const html = `
        <html>
          <head>
            <meta charset="UTF-8" />
          </head>

          <body>
            <table border="1">

              <tr>
                <th colspan="8">
                  ${this.escapeHtml(this.hospitalName)}
                  - Revenue Report
                </th>
              </tr>

              <tr>
                <th colspan="8">
                  ${this.fromDate}
                  to
                  ${this.toDate}
                </th>
              </tr>

              <tr>
                <th>Sr No.</th>
                <th>Date</th>
                <th>Invoice No.</th>
                <th>Patient Name</th>
                <th>Category</th>
                <th>Doctor Referred</th>
                <th>Payment Mode</th>
                <th>Total Received</th>
              </tr>

              ${rows}

              <tr>
                <td colspan="7">
                  <strong>
                    Total Revenue
                  </strong>
                </td>

                <td>
                  <strong>
                    ${this.summary.totalRevenue.toFixed(2)}
                  </strong>
                </td>
              </tr>

            </table>
          </body>
        </html>
      `;

      const blob = new Blob([html], {
        type: "application/vnd.ms-excel;charset=utf-8",
      });

      this.downloadBlob(
        blob,

        `Hospital-Revenue-${this.fromDate}-to-${this.toDate}.xls`,
      );

      this.toastr.success("Excel report downloaded successfully.");
    } catch (error) {
      console.error("Excel export failed:", error);

      this.toastr.error("Failed to export Excel report.");
    } finally {
      this.exportingExcel = false;
    }
  }

  // ============================================================
  // PDF
  // ============================================================

  exportPdf(): void {
    if (!this.filteredRows.length) {
      this.toastr.warning("No revenue data available to export.");

      return;
    }

    this.exportingPdf = true;

    const rows = this.filteredRows
      .map(
        (row, index) => `
        <tr>
          <td>
            ${index + 1}
          </td>

          <td>
            ${this.escapeHtml(this.formatDisplayDate(row.receivedDate))}
          </td>

          <td>
            ${this.escapeHtml(row.invoiceNumber)}
          </td>

          <td>
            ${this.escapeHtml(row.patientName)}
          </td>

          <td>
            ${this.escapeHtml(row.patientType)}
          </td>

          <td>
            ${this.escapeHtml(row.doctorName)}
          </td>

          <td>
            ${this.escapeHtml(row.paymentMode)}
          </td>

          <td class="amount">
            ₹${Number(row.totalReceived || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            })}
          </td>
        </tr>
      `,
      )
      .join("");

    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      this.exportingPdf = false;

      this.toastr.warning("Please allow popups to generate PDF.");

      return;
    }

    const html = `
      <!DOCTYPE html>

      <html>

      <head>

        <title>
          Revenue Report
        </title>

        <style>
          @page {
            size: A4 landscape;
            margin: 12mm;
          }

          body {
            font-family: Arial, sans-serif;
            color: #011044;
            margin: 0;
            font-size: 11px;
          }

          .header {
            margin-bottom: 18px;
            border-bottom: 2px solid #ba025e;
            padding-bottom: 12px;
          }

          h1 {
            margin: 0;
            font-size: 22px;
          }

          .hospital {
            margin-top: 5px;
            font-size: 14px;
            font-weight: 700;
          }

          .period {
            color: #667085;
            margin-top: 6px;
          }

          .summary {
            display: flex;
            gap: 15px;
            margin: 15px 0;
          }

          .summary-box {
            border: 1px solid #ddd;
            padding: 10px 15px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th,
          td {
            border: 1px solid #dfe4ee;
            padding: 7px;
            text-align: left;
          }

          th {
            background: #f1f4f9;
            font-size: 10px;
            text-transform: uppercase;
          }

          .amount {
            text-align: right;
            font-weight: 700;
          }

          .total-row td {
            background: #f5f7fb;
            font-weight: 700;
          }

          .footer {
            margin-top: 15px;
            color: #667085;
            font-size: 9px;
          }
        </style>

      </head>

      <body>

        <div class="header">

          <h1>
            Revenue / Day Book Report
          </h1>

          <div class="hospital">
            ${this.escapeHtml(this.hospitalName)}
          </div>

          <div class="period">
            Report Period:
            ${this.fromDate}
            to
            ${this.toDate}
          </div>

        </div>

        <div class="summary">

          <div class="summary-box">
            Total Revenue:
            <strong>
              ₹${this.summary.totalRevenue.toLocaleString("en-IN")}
            </strong>
          </div>

          <div class="summary-box">
            OPD:
            <strong>
              ₹${this.summary.opdRevenue.toLocaleString("en-IN")}
            </strong>
          </div>

          <div class="summary-box">
            IPD:
            <strong>
              ₹${this.summary.ipdRevenue.toLocaleString("en-IN")}
            </strong>
          </div>

          <div class="summary-box">
            Patients:
            <strong>
              ${this.summary.totalPatients}
            </strong>
          </div>

        </div>

        <table>

          <thead>
            <tr>
              <th>Sr.</th>
              <th>Date</th>
              <th>Invoice No.</th>
              <th>Patient</th>
              <th>Category</th>
              <th>Doctor Referred</th>
              <th>Payment Mode</th>
              <th>Total Received</th>
            </tr>
          </thead>

          <tbody>

            ${rows}

            <tr class="total-row">
              <td colspan="7">
                Total Received
              </td>

              <td class="amount">
                ₹${this.summary.totalRevenue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </td>
            </tr>

          </tbody>

        </table>

        <div class="footer">
          Generated from Nishwik Hospital Revenue Report.
        </div>

      </body>
      </html>
    `;

    printWindow.document.open();

    printWindow.document.write(html);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      this.exportingPdf = false;

      printWindow.print();
    }, 500);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;

    anchor.download = fileName;

    document.body.appendChild(anchor);

    anchor.click();

    document.body.removeChild(anchor);

    URL.revokeObjectURL(url);
  }

  private formatDisplayDate(value: string | Date): string {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",

      month: "short",

      year: "numeric",
    });
  }

  private escapeHtml(value: any): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private parseInputDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split("-").map(Number);

    if (parts.length !== 3) {
      return null;
    }

    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  private getDateTime(value: string | Date): number {
    const date = new Date(value);

    return isNaN(date.getTime()) ? 0 : date.getTime();
  }
}
